import React from 'react';

import { Card, CardBody, CardHeader, Text } from '@change/ds-core-ui';
import { CloseIcon } from '@change/ds-icons/CloseIcon';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { SocialPostPlatformId } from '../../hooks/useSocialPostUrlForm/platforms';

import { ChipThumbnail } from './components/ChipThumbnail';
import { SourceLine } from './components/SourceLine';

export const LinkChip: React.FC<Props> = ({
	platform,
	sourceLabel,
	sourceLogo,
	handle,
	title,
	description,
	image,
	onRemove,
	'data-qa': dataQa,
}) => {
	const { translate } = useI18n();

	// TikTok links identify a creator rather than an outlet, so the line reads "TikTok • @handle"
	// (or a generic video label for short share links that carry no handle).
	const detail =
		platform === 'tiktok' ? (handle ?? translate('corgi.homepage.hero_section.input_chip_fallback_label')) : undefined;
	const sourceLine = detail ? `${sourceLabel} • ${detail}` : sourceLabel;

	return (
		<div className="w-full px-2 pt-2">
			<Card
				as="button"
				type="button"
				orientation="horizontal"
				reverse
				aria-label={translate('corgi.homepage.hero_section.remove_link_button_label')}
				className="relative w-full [grid-template-columns:8rem_1fr] text-left [--card-gap:0.2rem] [--card-padding:0.5rem]"
				data-qa={dataQa}
				onClick={onRemove}
				elevation
			>
				<CardHeader className="flex items-center justify-center">
					<ChipThumbnail image={image} platform={platform} />
				</CardHeader>
				<CardBody className="flex min-w-0">
					<div className="flex min-w-0 flex-1 flex-col justify-center pr-8">
						<SourceLine platform={platform} label={sourceLine} logo={sourceLogo} />
						{title && (
							<Text size="small" fontWeight="bold" className={description ? 'line-clamp-2' : 'line-clamp-3'}>
								{title}
							</Text>
						)}
						{description && (
							<Text as="p" size="x-small" className="text-typography-weak line-clamp-2 break-words">
								{description}
							</Text>
						)}
					</div>
				</CardBody>
				<div
					aria-hidden="true"
					className="bg-fill-weaker text-icon-weak absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full"
				>
					<CloseIcon size="x-small" />
				</div>
			</Card>
		</div>
	);
};

type Props = Readonly<{
	platform: SocialPostPlatformId;
	/** The source's name (a publication) or domain (a social platform). */
	sourceLabel: string;
	/** The source's logo or favicon; the badge falls back to the platform icon without one. */
	sourceLogo?: string;
	/** Only shown for TikTok, whose links identify the creator rather than an outlet. */
	handle?: string;
	title?: string;
	description?: string;
	image?: string;
	onRemove: () => void;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'data-qa': string;
}>;
